import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useAtomValue, useSetAtom } from "jotai";
import classNames from "classnames";
import { ENABLE_SLOT } from "@/core/variables";
import { Intent } from "@/model";
import { clickFromKeyboard } from "@/util/keyboardUtil";
import { useDeleteIntent } from "@/query/intent";
import { intentSearchKeywordAtom, intentSearchTypeAtom, intentTableEditAtom } from "@/atom/intentTable";
import { useDialog, useModal } from "@/hook";
import HighlightText from "@/component/HighlightText";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { errorToast, successToast } from "@/component/Toast";
import { DeleteIcon, ToolIcon, PreferenceIcon } from "@/assets/svgs/icons";

import { INTENT_SETTING_MODAL_ID } from "./IntentSettingModal";
import { INTENT_SLOT_MODAL_ID } from "./IntentSlotModal";


export interface IntentFragmentProps {
    intent: Intent;
    open: boolean;
    checked: boolean;
    onCheck: (id: string, checked: boolean) => void;
    onClickOpen: (id: string, open: boolean) => void;
}

const IntentFragment: React.FC<IntentFragmentProps> = (props) => {
    const { intent, checked, open, onCheck, onClickOpen } = props;

    const intl = useIntl();
    const modal = useModal();
    const dialog = useDialog();

    const setIntentTableEdit = useSetAtom(intentTableEditAtom);
    const searchKeyword = useAtomValue(intentSearchKeywordAtom);
    const searchType = useAtomValue(intentSearchTypeAtom);

    const { mutateAsync: deleteIntent } = useDeleteIntent(
        intent.agent_id,
        intent.skill_id,
        intent.id
    );

    const onClickDelete = () => {
        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i03029" }),
            content: intl.formatMessage({ id: "i03030" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: async () => {
                deleteIntent()
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t005" }));
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t006" }));
                    });
            }
        });
    };

    useEffect(() => {
        if (!open)
            setIntentTableEdit(prev => {
                delete prev[`${intent.id}_sentence_edit`];
                return prev;
            });
        // eslint-disable-next-line
    }, [open, intent.id]);

    return (
        <div tabIndex={0}
             role="row"
             className={classNames(
                 "group h-table-row w-full pr-4 flex justify-between items-center",
                 "border-y border-transparent",
                 intent.active && "cursor-pointer",
                 checked
                    ? "bg-chat42-5 !border-b-chat42-5"
                    : "bg-white hover:bg-black-100 hover:border-black-100",
                 open
                    ? "!border-t-black-200 first:!border-t-transparent"
                    : (!checked && "!border-b-black-150")
             )}
             onClick={() => onClickOpen(intent.id, !open)}
             onKeyDown={clickFromKeyboard}
        >
            <div className="flex items-center">
                <div role="cell"
                     className="selection-table-td w-[34px]"
                >
                    <input type="checkbox"
                           className={classNames(
                               checked ? "opacity-100" : "opacity-0",
                               "selection-table-checkbox group-interaction"
                           )}
                           checked={checked}
                           onClick={e => e.stopPropagation()}
                           onChange={e => onCheck(intent.id, e.currentTarget.checked)}
                    />
                </div>
                <div role="cell"
                     className={classNames(
                         "text-black-500 text-[14px] font-[400] leading-[1.5]",
                         !intent.active && "line-through"
                     )}
                >
                    {searchType === "intent" ? (
                        <HighlightText query={intent.name}
                                       keyword={searchKeyword}
                                       highlightClassName="text-highlight"
                        />
                    ) : (
                        <span>{intent.name}</span>
                    )}         
                    <span>
                        {` (${intent.sentences.length})`}
                    </span>
                </div>
            </div>

            <div role="cell">
                <Popover>
                    {({ open, close }) => (
                        <>
                            <PopoverButton className="selection-table-popover-btn"
                                        open={open}
                                        close={close}
                            />
                            <PopoverMenu>
                                <PopoverItem className="selection-table-popover-item"
                                            onClick={() => modal.open(INTENT_SETTING_MODAL_ID, intent)}
                                >
                                    <PreferenceIcon className="size-3.5" />
                                    {intl.formatMessage({ id: "i03023" })}
                                </PopoverItem>
                                <PopoverItem className="selection-table-popover-item text-point-red"
                                            onClick={onClickDelete}
                                >
                                    <DeleteIcon className="size-3.5" />
                                    {intl.formatMessage({ id: "i03029" })}
                                </PopoverItem>
                                {ENABLE_SLOT && (
                                    <PopoverItem className="selection-table-popover-item"
                                                onClick={() => {
                                                    modal.open(INTENT_SLOT_MODAL_ID, {
                                                        intent: intent,
                                                        slots: intent.slot_info || []
                                                    });
                                                }}
                                    >
                                        <ToolIcon className="size-3.5" />
                                        {intl.formatMessage({ id: "i03031" })}
                                    </PopoverItem>
                                )}
                            </PopoverMenu>
                        </>
                    )}
                </Popover>
            </div>
        </div>
    );
};

export default IntentFragment;
